import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { ApiLoadState } from './ApiLoadState';
import { useAuth } from '../../context/AuthContext';
import { useGetMyToolByIdQuery } from '../../redux/myToolsApi';
import { formatInt, formatQty } from '../../utils/billToOrderCalc';
import {
  formatToolCreatedAt,
  formatToolDate,
  mapMyToolRow,
  myToolProductCount,
  toolTypeLabel,
  unwrapMyTool,
} from '../../utils/billToFlowTools';

const LIST_PATH = '/dashboard/admin/build-to-flow';

const SKU_COLUMNS = [
  'SKU',
  'SKU Name',
  'SPS 1',
  'SPS 2',
  'SPS 3',
  'Lead Time (Days)',
  'Buffer %',
  'Growth %',
  'Projected Sales',
  'Projected GC',
  'Build To',
  'Closing Stock',
  'Suggested Order',
  'Final Order',
  'Final Order by Outlet',
  'SPS Ord 1',
  'SPS Ord 2',
  'SPS Ord 3',
];

function displayValue(value) {
  if (value == null || value === '') return '—';
  return value;
}

function displayMetric(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return formatInt(n);
}

function displayQty(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return formatQty(n, 1);
}

function statusLabel(status) {
  if (status === true || status === 'true' || status === 1) return 'Active';
  if (status === false || status === 'false' || status === 0) return 'Inactive';
  
  const raw = String(status || '').trim();
  if (!raw) return '—';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function DetailRow({ label, value, accent }) {
  return (
    <div className="grid gap-1 border-b border-gray-100 py-3 sm:grid-cols-2 sm:gap-8">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-sm font-medium ${accent ? 'text-brand' : 'text-gray-900'}`}>
        {displayValue(value)}
      </p>
    </div>
  );
}

function AdminMyToolsView() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const { id } = useParams();
  const { data, isLoading, isFetching, error, refetch } = useGetMyToolByIdQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const tool = useMemo(() => {
    const unwrapped = unwrapMyTool(data);
    return unwrapped ? mapMyToolRow(unwrapped) : null;
  }, [data]);

  const products = useMemo(
    () => (Array.isArray(tool?.products) ? tool.products : []),
    [tool]
  );

  const [skuSearch, setSkuSearch] = useState('');
  const visibleProducts = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((item) =>
      [item.itemCode, item.itemName, item.category, item.subCategory]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [products, skuSearch]);
  const loadFailed = Boolean(error && !tool);

  return (
    <DashboardLayout title="Tool Details">
      <div className="space-y-4 py-4">
        <div className="flex justify-end">
          <p className="text-sm text-gray-500">
            <Link
              to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
              className="text-brand hover:underline"
            >
              Home
            </Link>
            {' / '}
            <Link to={LIST_PATH} className="text-brand hover:underline">
              My Tools
            </Link>
            {' / '}
            <span className="text-gray-400">View</span>
          </p>
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {loadFailed ? (
            <ApiLoadState
              isLoading={isLoading}
              isFetching={isFetching}
              hasData={false}
              error={error}
              refetch={refetch}
              message="Loading tool…"
              errorMessage={error?.data?.message || 'Unable to load tool details.'}
              className="py-10"
            />
          ) : isLoading && !tool ? (
            <ApiLoadState
              isLoading
              isFetching={isFetching}
              hasData={false}
              message="Loading tool…"
              className="py-10"
            />
          ) : !tool ? (
            <p className="py-10 text-center text-sm text-gray-500">Tool not found.</p>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900">Tool Details</h2>
              <div className="mt-4 max-w-3xl">
                <DetailRow label="Tool Name" value={tool.toolName} accent />
                <DetailRow label="Tool Code" value={tool.toolCode} />
                <DetailRow label="Tool Type" value={toolTypeLabel(tool)} />
                <DetailRow label="Location" value={tool.locationName} accent />
                <DetailRow label="Date" value={formatToolDate(tool.toolDate)} />
                <DetailRow label="Projected Sales" value={displayMetric(tool.totalProjectSale)} />
                <DetailRow label="Projected GC" value={displayMetric(tool.totalProjectGc)} />
                <DetailRow label="SKUs" value={myToolProductCount(tool) || '—'} />
                <DetailRow label="Status" value={statusLabel(tool.status)} />
                <DetailRow label="Created On" value={formatToolCreatedAt(tool.createdAt)} />
                <DetailRow label="Updated On" value={formatToolCreatedAt(tool.updatedAt)} />
                <DetailRow label="Created By" value={tool.createdBy} />
                <DetailRow label="Description" value={tool.description || '—'} />
              </div>

              <div className="mt-8 mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  SKU Planning ({products.length})
                </h3>
                {products.length ? (
                  <div className="relative w-full min-w-0 sm:max-w-xs">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search SKU NAME or Id"
                      value={skuSearch}
                      onChange={(e) => setSkuSearch(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                  </div>
                ) : null}
              </div>
              {!products.length ? (
                <p className="mt-3 text-sm text-gray-500">No SKUs found.</p>
              ) : (
                <>
                  <ul className="mt-3 space-y-3 lg:hidden">
                    {visibleProducts.length === 0 ? (
                      <li className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3 text-center text-sm text-gray-500">
                        No SKUs match your search.
                      </li>
                    ) : (
                    visibleProducts.map((item, index) => (
                      <li
                        key={item.id || `${item.itemCode}-${index}`}
                        className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                      >
                        <p className="text-sm font-semibold text-gray-900">
                          {item.itemName || '—'}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">SKU: {item.itemCode || '—'}</p>
                        <p className="mt-1 text-xs text-gray-600">
                          {item.category || '—'}
                          {item.subCategory ? ` · ${item.subCategory}` : ''}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Lead {displayValue(item.leadTime)} · Buffer {displayValue(item.buffer)} ·
                          Growth {displayValue(item.growth)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          SPS {displayValue(item.sps1)} / {displayValue(item.sps2)} /{' '}
                          {displayValue(item.sps3)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Projected Sales: {displayMetric(item.projectSale)} · GC:{' '}
                          {displayMetric(item.projectGc)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Build To {displayQty(item.buildTo)} · Closing {displayQty(item.closingStock)}{' '}
                          · Suggested {displayQty(item.suggestedOrder)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Final {displayQty(item.finalOrder)} · By Outlet{' '}
                          {displayQty(item.finalOrderByOutlet)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          SPS Ord {displayValue(item.spsOrd1)} / {displayValue(item.spsOrd2)} /{' '}
                          {displayValue(item.spsOrd3)}
                        </p>
                      </li>
                    ))
                    )}
                  </ul>
                  <div className="mt-3 hidden overflow-x-auto lg:block">
                    <table className="min-w-[1880px] border-collapse text-sm">
                      <thead>
                        <tr>
                          {SKU_COLUMNS.map((label) => (
                            <th
                              key={label}
                              className="whitespace-nowrap border-b border-gray-200 bg-[#F7E5D5] px-3 py-2 text-left font-semibold text-gray-700"
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleProducts.length === 0 ? (
                          <tr>
                            <td
                              colSpan={SKU_COLUMNS.length}
                              className="px-3 py-8 text-center text-sm text-gray-500"
                            >
                              No SKUs match your search.
                            </td>
                          </tr>
                        ) : (
                        visibleProducts.map((item, index) => (
                          <tr
                            key={item.id || `${item.itemCode}-${index}`}
                            className="hover:bg-amber-50/40"
                          >
                            <td className="border-b border-gray-100 px-3 py-2 text-brand">
                              {item.itemCode || '—'}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2 font-medium text-gray-900">
                              <span className="block">{item.itemName || '—'}</span>
                              <span className="block text-xs font-normal text-gray-500">
                                {item.subCategory || item.category || '—'}
                              </span>
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.sps1)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.sps2)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.sps3)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.leadTime)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.buffer)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.growth)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayMetric(item.projectSale)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2 font-medium">
                              {displayMetric(item.projectGc)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayQty(item.buildTo)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayQty(item.closingStock)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayQty(item.suggestedOrder)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayQty(item.finalOrder)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayQty(item.finalOrderByOutlet)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.spsOrd1)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.spsOrd2)}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2">
                              {displayValue(item.spsOrd3)}
                            </td>
                          </tr>
                        ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminMyToolsView;
